/* App boot + URL routing. Every screen has a real URL and refresh/deep-links work:
     /                                  dashboard
     /new                               new-analysis setup
     /scan/<id>/analyzing               live progress
     /scan/<id>/summary                 "We found N opportunities" landing
     /report/<id>/overview|opportunities|winners|pages|api-logs
     /report/<id>/opportunities/<topicId>          topic detail
     /report/<id>/winners/<winnerId>               winner detail
     /report/<id>/pages?page=<encoded url>         page detail
   History back/forward work; a refresh re-fetches the scan and lands you where you were.
   Every navigation gives immediate feedback; view render errors show a recoverable
   card instead of a blank page. */

import { api } from "./api.js";
import { state, newSetupDraft } from "./state.js";
import { renderAuthScreen } from "./screens/auth.js";
import { renderHome, wireHome } from "./screens/home.js";
import { renderSetup } from "./screens/setup.js";
import { startAnalyzing } from "./screens/analyzing.js";
import { renderSummary } from "./screens/summary.js";
import { renderReport } from "./screens/report.js";
import { renderAccountModal } from "./screens/account.js";
import { T, FONT, escapeHtml } from "./ui.js";
import { revive } from "@agf/analyzer";

const app = document.querySelector("#app");

/* ---------- history helpers ---------- */

function navigate(path, { replace = false } = {}) {
  if (location.pathname === path) return;
  if (replace) history.replaceState({ path }, "", path);
  else history.pushState({ path }, "", path);
}

/* URL words ↔ internal view ids. The sidebar calls them opps/dfslogs; the URL says
   opportunities/api-logs. Forgetting this mapping made every deep link land on
   Overview (and crash the shell before it painted). */
const URL_TO_VIEW = { overview: "overview", opportunities: "opps", winners: "winners", pages: "pages", "api-logs": "dfslogs" };

function reportPath(scanId, r) {
  const base = `/report/${scanId}`;
  if (r.view === "opps") return r.topicId ? `${base}/opportunities/${r.topicId}` : `${base}/opportunities`;
  if (r.view === "winners") return r.winnerId ? `${base}/winners/${r.winnerId}` : `${base}/winners`;
  if (r.view === "pages") return r.pageUrl ? `${base}/pages?page=${encodeURIComponent(r.pageUrl)}` : `${base}/pages`;
  if (r.view === "dfslogs") return `${base}/api-logs`;
  return `${base}/overview`;
}

window.addEventListener("popstate", () => { route().catch(showErrorCard); });

/* ---------- loading / error feedback ---------- */

function showLoading(message) {
  app.innerHTML = `
    <div style="min-height:100vh;background:${T.bg};display:flex;align-items:center;justify-content:center;font-family:${FONT}">
      <div style="display:flex;align-items:center;gap:12px;color:${T.sub};font-size:13.5px;font-weight:600">
        <span style="width:18px;height:18px;border-radius:50%;border:2.5px solid ${T.violetSoft};border-top-color:${T.violet};display:inline-block;animation:agf-spin .7s linear infinite"></span>
        ${message}
      </div>
    </div>
    <style>@keyframes agf-spin{to{transform:rotate(360deg)}}</style>`;
}

function showErrorCard(err) {
  console.error("[app error]", err);
  app.innerHTML = `
    <div style="min-height:100vh;background:${T.bg};display:flex;align-items:center;justify-content:center;padding:24px;font-family:${FONT}">
      <div style="background:${T.surface};border:1px solid ${T.line};border-radius:16px;padding:26px;max-width:460px;width:100%">
        <div style="font-weight:800;font-size:16px;margin-bottom:8px">Something went wrong</div>
        <div style="font-size:12.5px;color:${T.sub};line-height:1.6;margin-bottom:16px">${escapeHtml(String((err && err.message) || err))} — the technical detail is in the browser console.</div>
        <button id="err-reload" style="border:1px solid ${T.line};background:${T.surface};border-radius:9px;padding:8px 14px;font-family:${FONT};font-size:12.5px;font-weight:700;cursor:pointer;margin-right:8px">Reload page</button>
        <button id="err-home" style="border:none;background:${T.violet};color:#fff;border-radius:9px;padding:8px 14px;font-family:${FONT};font-size:12.5px;font-weight:700;cursor:pointer">Back to dashboard</button>
      </div>
    </div>`;
  app.querySelector("#err-reload").addEventListener("click", () => location.reload());
  app.querySelector("#err-home").addEventListener("click", () => { navigate("/", { replace: true }); showHome(); });
}

/* ---------- boot ---------- */

async function boot() {
  try {
    await refreshMe();
  } catch (err) {
    if (err.status === 401) { showAuth(); return; }
    showErrorCard(err);
    return;
  }
  route().catch(showErrorCard);
}

async function refreshMe() {
  const me = await api.me();
  state.user = me.user;
  state.hasDfs = me.hasDfs;
  state.googleEnabled = me.googleEnabled;
  state.googleClientId = me.googleClientId;
  state.allowedDomains = me.allowedDomains || [];
}

/* ---------- the router ---------- */

async function route() {
  closeAccountModal();
  const path = location.pathname;

  if (path === "/" || path === "") {
    if (!state.user) { showAuth(); return; }
    await showHome();
    return;
  }

  if (!state.user) { showAuth(); return; } // deep link while logged out; sign-in resumes it

  if (path === "/new") { state.mode = state.mode || "gsc"; drawSetup(); return; }

  let m = path.match(/^\/scan\/([\w-]+)\/analyzing$/);
  if (m) { state.scanId = m[1]; startAnalyzingScreen(); return; }

  m = path.match(/^\/scan\/([\w-]+)\/summary$/);
  if (m) { await openReport(m[1], { view: "overview" }, { then: "summary" }); return; }

  m = path.match(/^\/report\/([\w-]+)(?:\/([\w-]+))?/);
  if (m) {
    const view = URL_TO_VIEW[m[2] || "overview"];
    if (!view) { navigate(`/report/${m[1]}/overview`, { replace: true }); await openReport(m[1], { view: "overview" }, {}); return; }
    const r = { view };
    const q = new URLSearchParams(location.search);
    if (view === "opps") { const tm = path.match(/^\/report\/[\w-]+\/opportunities\/([\w-]+)$/); if (tm) r.topicId = tm[1]; }
    if (view === "winners") { const wm = path.match(/^\/report\/[\w-]+\/winners\/([\w-]+)$/); if (wm) r.winnerId = wm[1]; }
    if (view === "pages" && q.get("page")) r.pageUrl = q.get("page");
    await openReport(m[1], r, {});
    return;
  }

  navigate("/", { replace: true }); // unknown URL → dashboard
  await showHome();
}

/* Fetch a scan's results if not already in memory, then land on the report/summary. */
async function openReport(scanId, reportRoute, { then = "report" } = {}) {
  state.scanId = scanId;
  if (!state.results || state.activeScan?.id !== scanId) {
    showLoading("Opening your report…");
    try {
      state.results = revive(await api.scanResults(scanId));
      state.activeScan = { id: scanId };
      state.serpLogs = [];
      if (state.results.meta.hasSerp) {
        try { state.serpLogs = (await api.logs()).logs; } catch { state.serpLogs = []; }
      }
    } catch (err) {
      if (err.status === 409) { // not finished yet → watch it instead
        navigate(`/scan/${scanId}/analyzing`, { replace: true });
        startAnalyzingScreen();
        return;
      }
      throw err;
    }
  }
  if (then === "summary") {
    navigate(`/scan/${scanId}/summary`);
    drawSummary();
  } else {
    navigate(reportPath(scanId, reportRoute));
    drawReport(reportRoute);
  }
}

/* ---------- screens ---------- */

function showAuth() {
  state.screen = "auth";
  renderAuthScreen(app);
}

function drawHome() {
  state.screen = "home";
  renderHome(app, { user: state.user, hasDfs: state.hasDfs, scans: state.scans });
  wireHome(app, {
    onRun: mode => {
      state.mode = mode;
      state.setupDraft = null; // a fresh analysis starts with empty upload zones
      navigate("/new");
      drawSetup();
    },
    onDemo: async withSerp => {
      const { scanId } = await api.demo(withSerp);
      state.scanId = scanId;
      navigate(`/scan/${scanId}/analyzing`);
      startAnalyzingScreen();
    },
    onOpenScan: (id, status) => {
      if (status === "completed") { openReport(id, { view: "overview" }, { then: "summary" }).catch(showErrorCard); return; }
      if (status === "failed") {
        api.scan(id).then(scan => alert(scan.error || "This scan failed — please run it again.")).catch(err => alert(err.message));
        return;
      }
      state.scanId = id;
      navigate(`/scan/${id}/analyzing`);
      startAnalyzingScreen();
    },
    onDeleteScan: async id => {
      if (!window.confirm("Delete this scan?")) return false;
      try {
        await api.deleteScan(id);
      } catch (err) {
        window.alert(`Could not delete that scan: ${err.message}`);
        return false;
      }
      state.scans = state.scans.filter(s => s.id !== id);
      drawHome(); // repaint immediately — the row used to linger until a manual refresh
      return true;
    },
    onAccount: () => openAccount(false),
  });
}

async function showHome() {
  navigate("/");
  drawHome(); // paint immediately from cache
  try {
    const { scans } = await api.scans();
    if (state.screen === "home" && JSON.stringify(scans) !== JSON.stringify(state.scans)) {
      state.scans = scans;
      drawHome();
    }
  } catch { /* keep the cached list */ }
}

function drawSetup() {
  state.screen = "setup";
  if (!state.setupDraft) state.setupDraft = newSetupDraft();
  renderSetup(app, {
    mode: state.mode,
    hasDfs: state.hasDfs,
    draft: state.setupDraft,
    actions: {
      onBack: () => { state.setupDraft = null; navigate("/"); showHome(); },
      onAccount: prompt => openAccount(!!prompt),
      onRun: async formData => {
        const { scanId } = await api.createScan(formData);
        state.setupDraft = null; // the files are on the server now
        state.scanId = scanId;
        navigate(`/scan/${scanId}/analyzing`);
        startAnalyzingScreen();
      },
    },
  });
}

function startAnalyzingScreen() {
  state.screen = "analyzing";
  startAnalyzing(app, {
    scanId: state.scanId,
    onCompleted: () => openCompleted(state.scanId),
    onFailed: () => { /* error shown inline */ },
    onCancel: () => { navigate("/"); showHome(); },
  });
}

async function openCompleted(scanId) {
  await openReport(scanId, { view: "overview" }, { then: "summary" });
}

function drawSummary() {
  state.screen = "summary";
  renderSummary(app, {
    R: state.results,
    onEnter: view => {
      navigate(reportPath(state.scanId, { view }));
      drawReport({ view });
    },
  });
}

function drawReport(reportRoute) {
  state.screen = "app";
  renderReport(app, {
    R: state.results,
    route: reportRoute,
    hasDfs: state.hasDfs,
    serpLogs: state.serpLogs || [],
    onDashboard: () => { navigate("/"); showHome(); },
    onOpenAccount: () => openAccount(false),
    onNavigate: r => {
      navigate(reportPath(state.scanId, r));
      drawReport(r);
    },
  });
}

/* ---------- account modal ---------- */

function closeAccountModal() {
  const m = document.getElementById("account-modal");
  if (m) m.remove();
}

/* Closing the modal must repaint what is behind it: both the dashboard and the setup
   screen show whether DataForSEO credentials exist, and that can have just changed. */
function closeAccount() {
  closeAccountModal();
  if (state.screen === "home") drawHome();
  else if (state.screen === "setup") drawSetup();
}

function openAccount(promptForSerp) {
  closeAccountModal();
  renderAccountModal(app, {
    user: state.user,
    hasDfs: state.hasDfs,
    promptForSerp,
    onSaveDfs: async saved => {
      await refreshMe().catch(() => {});
      if (promptForSerp && saved && state.hasDfs) {
        closeAccountModal();
        state.mode = "serp";
        navigate("/new");
        drawSetup();
      } else {
        openAccount(promptForSerp);
      }
    },
    onSignOut: signOut,
    onClose: closeAccount,
  });
}

/* helpers */

async function signOut() {
  try { await api.signOut(); } catch { /* session dies client-side regardless */ }
  closeAccountModal();
  state.user = null;
  state.hasDfs = false;
  state.scans = [];
  state.results = null;
  showAuth();
}

window.addEventListener("agf:signed-in", () => { route().catch(showErrorCard); });

boot();
